import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

// ── Legal ─────────────────────────────────────────────────────────────────────
// Privacy Policy + Terms of Service, one component, routed at /privacy and /terms.
// Plain-language and honest — NextEp collects little, runs no ad-tracking, and makes
// export + real deletion first-class. This is a solid starting draft; have counsel
// review before relying on it commercially.

const EFFECTIVE = 'July 8, 2026'
const CONTACT   = 'hello@nextep.show'

const S  = ({ children }) => <h2 style={{ color:'var(--text-strong)', fontSize:17, fontWeight:800, letterSpacing:'-0.01em', margin:'26px 0 8px' }}>{children}</h2>
const P  = ({ children }) => <p style={{ color:'var(--text)', fontSize:14.5, lineHeight:1.65, margin:'0 0 10px' }}>{children}</p>
const LI = ({ children }) => <li style={{ color:'var(--text)', fontSize:14.5, lineHeight:1.6, margin:'0 0 6px' }}>{children}</li>
const UL = ({ children }) => <ul style={{ margin:'0 0 10px', paddingLeft:20 }}>{children}</ul>

export default function LegalPage({ doc = 'privacy' }) {
  const navigate = useNavigate()
  const isPrivacy = doc === 'privacy'
  const isDelete  = doc === 'delete'
  const title = isDelete ? 'Delete Your Account' : isPrivacy ? 'Privacy Policy' : 'Terms of Service'

  return (
    <div style={{ minHeight:'100dvh', background:'var(--bg)', fontFamily:'var(--font)', paddingBottom:'calc(var(--nav-h) + 16px)' }}>
      <div style={{ position:'sticky', top:0, zIndex:20, background:'var(--nav-bg)', backdropFilter:'blur(12px)',
        borderBottom:'1px solid var(--border)', padding:'max(14px,env(safe-area-inset-top)) 16px 14px', display:'flex', alignItems:'center', gap:12 }}>
        <button onClick={() => navigate(-1)} aria-label="Back" style={{ background:'none', border:'none', color:'var(--text)', cursor:'pointer', padding:4 }}>
          <ArrowLeft size={20} />
        </button>
        <div style={{ color:'var(--text-strong)', fontWeight:800, fontSize:18 }}>{title}</div>
      </div>

      <div style={{ maxWidth:680, margin:'0 auto', padding:'20px 18px 40px' }}>
        <div style={{ color:'var(--text-dim)', fontSize:12.5, marginBottom:6 }}>Effective {EFFECTIVE}</div>

        {isDelete ? (
          <>
            <P>You can permanently delete your NextEp account and everything in it at any time, right from the app. Here's how.</P>

            <S>Delete from the app</S>
            <UL>
              <LI>Open NextEp and sign in at <b>app.nextep.show</b> (or launch the installed app).</LI>
              <LI>Go to <b>Settings</b> (tap your profile picture, then Settings).</LI>
              <LI>Scroll to <b>Account</b> and tap <b>Delete account</b>.</LI>
              <LI>Confirm. Your account and all associated data are erased immediately and permanently.</LI>
            </UL>

            <S>Can't sign in?</S>
            <P>Email <b>{CONTACT}</b> from the address on your account and we'll delete it for you. We may ask a question or two to confirm the account is yours before we erase it.</P>

            <S>What gets deleted</S>
            <P>Everything tied to your account is removed: your login, profile, watchlist and progress, ratings, reviews, comments, lists, collection cards, follows, and notes. Deletion is immediate and permanent. We don't keep a shadow copy.</P>

            <S>What's kept</S>
            <P>Nothing personal is retained after deletion. Aggregate, anonymized counts that can't be traced back to you (for example, a show's total tracker count) may remain, and routine backups age out on their normal cycle.</P>
          </>
        ) : isPrivacy ? (
          <>
            <P>NextEp is a personal TV-tracking app. We try to collect as little as possible, we don't run advertising trackers, and we never sell your data. This policy explains what we hold and the control you have over it.</P>

            <S>What we collect</S>
            <UL>
              <LI><b>Your account</b>, the email address you sign in with, and a display name and username you choose.</LI>
              <LI><b>What you track</b>, the shows and episodes you add, your progress, statuses, ratings, reviews, lists, and any notes or memories you write.</LI>
              <LI><b>Imported history</b>, if you import from another service (e.g. TV Time or Trakt), the watch history in the file you provide, resolved to show records.</LI>
              <LI><b>Basic preferences</b>, settings like your timezone and notification choices, and, only if you turn them on, a browser push-notification subscription.</LI>
            </UL>

            <S>What we don't do</S>
            <P>No third-party advertising or analytics trackers run in the app. We don't sell, rent, or trade your personal data. We don't build advertising profiles.</P>

            <S>Who processes data for us</S>
            <P>We use a small set of service providers strictly to run NextEp:</P>
            <UL>
              <LI><b>Supabase</b>, stores your account and data and handles sign-in.</LI>
              <LI><b>Netlify</b>, hosts the app.</LI>
              <LI><b>The Movie Database (TMDB)</b>, supplies show information; we send show searches/IDs to fetch details. We are not endorsed or certified by TMDB.</LI>
              <LI><b>Resend</b>, sends email (e.g. your weekly briefing) only if you opt in.</LI>
            </UL>

            <S>Your rights &amp; controls</S>
            <UL>
              <LI><b>Export</b>, download your data any time from Settings → Export.</LI>
              <LI><b>Delete</b>, Settings → Delete account permanently erases your account and all associated data (watchlist, ratings, reviews, lists, cards, follows) and removes your login. It cannot be undone.</LI>
              <LI><b>Correct</b>, edit your profile and content at any time.</LI>
              <LI><b>Privacy</b>, set your profile private in Settings to keep it out of discovery.</LI>
            </UL>
            <P>If you're in the EU/UK or California, these cover your GDPR / CCPA rights to access, portability, correction, and erasure. To make a request or ask a question, email <b>{CONTACT}</b>.</P>

            <S>Retention</S>
            <P>We keep your data until you delete it. Deletion is immediate and permanent, we don't keep a shadow copy.</P>

            <S>Children</S>
            <P>NextEp isn't intended for anyone under 13 (or the minimum age in your country). We don't knowingly collect data from children.</P>

            <S>Changes</S>
            <P>If this policy changes materially, we'll update the date above and, where appropriate, notify you in the app. Questions: <b>{CONTACT}</b>.</P>
          </>
        ) : (
          <>
            <P>By using NextEp, you agree to these terms. If you don't agree, please don't use the app.</P>

            <S>What NextEp is</S>
            <P>NextEp is a personal tool for tracking the TV you watch, keeping lists, and sharing with people you choose. It's provided for your personal, non-commercial use.</P>

            <S>Your account</S>
            <P>Keep your sign-in secure; you're responsible for activity under your account. Tell us at {CONTACT} if you suspect unauthorized use.</P>

            <S>Acceptable use</S>
            <P>Don't use NextEp to break the law, harass others, post content you don't have the right to, spam, or scrape or overload the service. We may suspend accounts that abuse these terms. A confirmed copyright takedown against your content counts as a strike; repeated strikes lead to suspension.</P>

            <S>Your content</S>
            <P>Your reviews, lists, notes, and ratings are yours. You grant us the limited permission needed to store and display them according to your own privacy settings (for example, showing a public review to other members). Delete them, or your whole account, at any time.</P>
            <P>When you post an image (including memes) to a conversation, you represent that you have the right to share it, and you grant us the non-exclusive license needed to host and display it within NextEp. You can delete your posts at any time, which also deletes the hosted image.</P>

            <S>Copyright &amp; takedowns (DMCA)</S>
            <P>We respect intellectual property rights and respond to valid takedown notices under the Digital Millennium Copyright Act. To submit a notice, email <b>{CONTACT}</b> with:</P>
            <UL>
              <LI>The specific content and where it appears in the app (a link or clear description).</LI>
              <LI>Your contact information (name, address, phone, and email).</LI>
              <LI>A statement that you have a good-faith belief the use isn't authorized by the copyright owner, its agent, or the law.</LI>
              <LI>A statement, made under penalty of perjury, that the notice is accurate and that you're the copyright owner or authorized to act on their behalf.</LI>
              <LI>Your physical or electronic signature.</LI>
            </UL>
            <P>We remove content that's the subject of a valid notice and notify the poster. If you believe your content was removed in error, you may submit a counter-notice to the same address identifying the content and stating, under penalty of perjury, that you have a good-faith belief it was removed by mistake; we'll forward it to the original claimant and may restore the content per the DMCA's standard timeline unless they pursue further action. Accounts that repeatedly post infringing content will be suspended or terminated.</P>

            <S>Importing from other services</S>
            <P>You may import your own watch history from other apps. You're responsible for having the right to the data you upload. NextEp resolves it to show records in your private account.</P>

            <S>Third-party names &amp; data</S>
            <P>Show information is provided by The Movie Database (TMDB). This product uses the TMDB API but is not endorsed or certified by TMDB.</P>
            <P><b>NextEp is an independent product. It is not affiliated with, endorsed by, or connected to TV Time, Whip Media, Trakt, or any streaming service or network.</b> Those names and logos are trademarks of their respective owners and are used only to describe compatibility (for example, importing a file you exported from another service).</P>

            <S>No warranty</S>
            <P>NextEp is provided "as is," without warranties of any kind. We work to keep it reliable and accurate, but we don't guarantee it will be uninterrupted or error-free, and show data comes from third parties.</P>

            <S>Limitation of liability</S>
            <P>To the extent permitted by law, NextEp and its maker won't be liable for indirect, incidental, or consequential damages arising from your use of the app. Nothing here limits liability that can't be limited by law.</P>

            <S>Termination</S>
            <P>You can stop using NextEp and delete your account at any time. We may suspend or end access for accounts that violate these terms.</P>

            <S>Changes</S>
            <P>We may update these terms; we'll change the date above when we do. Continued use after an update means you accept it. Questions: <b>{CONTACT}</b>.</P>
          </>
        )}

        <div style={{ marginTop:28, paddingTop:16, borderTop:'1px solid var(--border)', display:'flex', gap:16 }}>
          <button onClick={() => navigate(isDelete ? '/privacy' : isPrivacy ? '/terms' : '/privacy')} style={{ background:'none', border:'none', color:'var(--amber)', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'var(--font)', padding:0 }}>
            {isDelete ? 'Privacy Policy →' : isPrivacy ? 'Terms of Service →' : 'Privacy Policy →'}
          </button>
        </div>
      </div>
    </div>
  )
}
